import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { actionCreators as selfServiceActions } from "../redux/actions/selfServiceActions";
import { createVeriffFrame, MESSAGES } from '@veriff/incontext-sdk';
import * as routeHelpers from "../authentication/routeHelpers";
import { Box, Modal } from "@material-ui/core";
import ConfirmID from "./ConfirmID";
import { Alert } from "@material-ui/lab";
import LoadingAlert from "./LoadingAlert";

function IDVerification(props) {
    const [isModalOpen, setIsModalOpen] = useState(true);
    const [idVerificationIsEnabled, setIdVerificationIsEnabled] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(null);
    const [documentsToVerify, setDocumentsToVerify] = useState([]);
    const [shouldUpload, setShouldUpload] = useState(false);
    const [activeEntityChangeRequestID, setActiveEntityChangeRequestID] = useState();
    const [errored, setErrored] = useState(false);
    const [loading, setLoading] = useState(false);
    const [nextDocumentIndex, setNextDocumentIndex] = useState(null);
    const [addCompleted, setAddCompleted] = useState(false);

    const params = new URLSearchParams(props.location.search);
    const forceNewDocument = params.get('new') === 'true';
    useEffect(() => {
        if (props && props.activeEntity && props.activeEntity.EntityChangeRequestID !== activeEntityChangeRequestID) {
            setActiveEntityChangeRequestID(props.activeEntity.EntityChangeRequestID);
        }

        props.checkIfIDVerificationIsEnabled().then((enabled) => {
            if (enabled !== idVerificationIsEnabled) {
                setIdVerificationIsEnabled(enabled);
            }
        });

    }, [props.activeEntity, activeEntityChangeRequestID])

    useEffect(() => {
        if (!props?.activeEntity?.IdentificationDocuments) {
            var route = routeHelpers.buildRoute(`/entities/${props.match.params.entityChangeRequestId}`);
            props.history.push(route);
            return;
        }

        updateDocumentsToVerify();
    }, [props?.activeEntity?.IdentificationDocuments, idVerificationIsEnabled]);

    useEffect(() => {
        if (nextDocumentIndex === null) return;

        if (nextDocumentIndex >= documentsToVerify.length) {
            var noDocument = props.activeEntity.IdentificationDocuments.filter(doc => doc.UserDecision !== 'Removed').length === 0;

            if (noDocument) {
                setCurrentIndex(null); //show the upload modal
                setShouldUpload(true);
            } else {
                setIsModalOpen(false);
                setCurrentIndex(null);
                var route = routeHelpers.buildRoute(`/entities/${props.activeEntity.EntityChangeRequestID}`);
                props.history.push(route);
            }
        } else {
            setCurrentIndex(nextDocumentIndex);
        }
    }, [nextDocumentIndex])

    const updateDocumentsToVerify = () => {
        var shouldVerify = idVerificationIsEnabled
            && props.activeEntity.EntityTypeCode === 'Individual';

        if (!shouldVerify) {
            var route = routeHelpers.buildRoute(`/entities/${props.activeEntity.EntityChangeRequestID}`);
            props.history.push(route);
            return;
        }

        var identificationDocuments = props.activeEntity.IdentificationDocuments;

        var shouldReviewDocument = identificationDocuments.some(doc => !doc.UserDecision)
            || identificationDocuments.some(doc => doc.UserDecision === 'Skipped')
            || identificationDocuments.some(doc => doc.VerificationStatus === 'Declined' && doc.UserDecision !== 'Removed')
            || identificationDocuments.length === 0;

        if (!shouldReviewDocument && (!forceNewDocument || addCompleted)) {
            var route = routeHelpers.buildRoute(`/entities/${props.activeEntity.EntityChangeRequestID}`);
            props.history.push(route);
            return;
        }

        if (forceNewDocument) {
            addDocument();
            return;
        }

        if (shouldVerify) {
            setIsModalOpen(true);
        }

        var shouldUploadId = shouldVerify && identificationDocuments.filter(doc => doc.UserDecision !== 'Removed').length === 0;

        if (shouldUpload !== shouldUploadId) {
            setShouldUpload(shouldUploadId);
        }

        if (shouldUploadId) {
            return;
        }

        var idDocumentsToVerify = identificationDocuments;
        setDocumentsToVerify(idDocumentsToVerify);

        if (idDocumentsToVerify.length > 0 && currentIndex === null) {
            setCurrentIndex(0);
        }
    }

    const skipDocument = () => {
        proceed('Skipped');
    }

    const removeDocument = (document) => {
        proceed('Removed');
    }

    const confirmDocument = () => {
        proceed('Confirmed');
    }

    const skipVerification = () => {
        props.activeEntity.IdentificationDocuments.filter(doc => !doc.UserDecision).forEach((doc) => { props.updateIdentificationDocumentField(doc, "UserDecision", 'Skipped'); });
        props.setIdVerificationSkipped(true);
        setCurrentIndex(null);
        var route = routeHelpers.buildRoute(`/entities/${props.activeEntity.EntityChangeRequestID}`);
        props.history.push(route);
    }

    const proceed = (userDecision) => {
        props.updateIdentificationDocumentField(documentsToVerify[currentIndex], "UserDecision", userDecision);
        nextDocument();
    }

    const addDocument = async () => {
        await uploadDocument();
    };

    const updateDocument = async () => {
        await uploadDocument(documentsToVerify[currentIndex].ID);
    };

    const clearUserDecision = async () => {
        props.updateIdentificationDocumentField(documentsToVerify[currentIndex], "UserDecision", null);
    };

    const nextDocument = async () => {
        setNextDocumentIndex(currentIndex + 1);
    };

    const uploadDocument = async (documentID) => {
        setLoading(true);

        var isNew = !documentID;

        var response = await props.getVerificationSession(props.activeEntity.EntityChangeRequestID, documentID || null);
        if (!response) {
            setLoading(false);
            setErrored(true);
        }
        else {
            createVeriffFrame({
                url: response.data.Url,
                onEvent: function (msg) {
                    switch (msg) {
                        case MESSAGES.STARTED:
                            {
                                const searchParams = new URLSearchParams(props.location.search);
                                searchParams.delete('new');
                                props.history.push({
                                    pathname: props.location.pathname,
                                    search: searchParams.toString(),
                                }, { replace: true });
                            }
                            break;
                        case MESSAGES.CANCELED:
                            {
                                const searchParams = new URLSearchParams(props.location.search);
                                searchParams.delete('new');
                                props.history.push({
                                    pathname: props.location.pathname,
                                    search: searchParams.toString(),
                                }, { replace: true });
                            }
                            break;
                        case MESSAGES.FINISHED:
                            if (isNew) {
                                setAddCompleted(true);
                                props.addIdentificationDocument({ UserDecision: 'New', TempID: response.data.DocumentId });
                                var route = routeHelpers.buildRoute(`/entities/${props.activeEntity.EntityChangeRequestID}`);
                                props.history.push(route);
                            } else {
                                proceed('Updated');
                            }
                            break;
                    }
                }
            });

            setLoading(false);
        }
    }

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 920,
        bgcolor: 'background.paper',
        boxShadow: 24,
        padding: '0 0 0 0'
    };

    return (
        <div className="id-verification">
            <Modal
                disableBackdropClick
                open={isModalOpen}
                onClose={() => skipVerification()}>
                <>
                    {shouldUpload && !forceNewDocument && <Box sx={modalStyle} className='verification-modal'>
                        <Box sx={{
                            borderRadius: 0,
                            p: 5, width: '100%', minWidth: 500,
                            '& .MuiTextField-root': {
                                margin: '25px', width: '25%'
                            },
                            '& .MuiButtonBase-root': {
                                marginLeft: '25px'
                            }
                        }}
                            component="form"
                            noValidate
                            autoComplete="off"
                        >
                            {loading && <LoadingAlert title="Please wait" />}
                            {!loading && <Alert severity="warning">You have no ID documents on file. Please <b onClick={addDocument} style={{ cursor: 'pointer' }}>add one</b> or <b onClick={skipVerification} style={{ cursor: 'pointer' }}>skip for now</b>.</Alert>}
                        </Box>
                    </Box>}
                    {currentIndex !== null && !shouldUpload && <Box sx={modalStyle} className='verification-modal'>
                        <ConfirmID skip={skipDocument} remove={removeDocument} confirm={confirmDocument} document={documentsToVerify[currentIndex]} upload={updateDocument} changeDecision={clearUserDecision} next={nextDocument} errored={errored} loading={loading}></ConfirmID>
                    </Box>}
                </>
            </Modal>
        </div>
    );
}

export default connect(
    (state) => state.selfService,
    (dispatch) => bindActionCreators(selfServiceActions, dispatch)
)(IDVerification);
